import { router } from "@inertiajs/react"
import { GripVertical, Image as ImageIcon, Trash2, Video } from "lucide-react"
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { FileUploadButton } from "./FileUploadButton"
import { AttachmentViewer } from "./AttachmentViewer"

export type AttachmentItem = {
  id: number
  kind: "image" | "video" | "featured_image"
  filename: string | null
  url: string | null
  content_type: string | null
  bytes: number | null
  position: number
}

// Match the server's MAX_IMAGES_PER_PIECE constant
// (app/services/attachments/creator.rb).
const MAX_IMAGES = 4

// Small leading preview: a thumbnail for images, an icon otherwise.
function AttachmentThumb({ attachment }: { attachment: AttachmentItem }) {
  if (attachment.kind !== "video" && attachment.url) {
    return (
      <img
        src={attachment.url}
        alt={attachment.filename ?? "Attachment"}
        className="h-9 w-9 shrink-0 rounded object-cover"
      />
    )
  }
  return attachment.kind === "video" ? (
    <Video className="h-4 w-4 text-ink-muted" />
  ) : (
    <ImageIcon className="h-4 w-4 text-ink-muted" />
  )
}

type Props = {
  pieceId: number
  attachments: AttachmentItem[]
  // Which kinds the channel surfaces; one "Add …" button per allowed
  // kind. Derived from the channel's fields_schema by the caller.
  allowedKinds: Array<"image" | "video">
  // When false the list is read-only — add/remove buttons and drag-reorder
  // are hidden. Used once a piece is past its editable window (published /
  // archived / mid-publish). Defaults to editable.
  editable?: boolean
}

export function AttachmentList({ pieceId, attachments, allowedKinds, editable = true }: Props) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const sorted = [...attachments].sort((a, b) => a.position - b.position)

  const imageCount = attachments.filter((a) => a.kind === "image").length
  const hasVideo   = attachments.some((a) => a.kind === "video")
  // When the channel surfaces both image and video fields (X, Bluesky,
  // LinkedIn), pick one or the other — never both. The server enforces
  // the same rule.
  const mutuallyExclusive = allowedKinds.includes("image") && allowedKinds.includes("video")

  const showImageButton = editable &&
    allowedKinds.includes("image") &&
    imageCount < MAX_IMAGES &&
    !(mutuallyExclusive && hasVideo)

  const showVideoButton = editable &&
    allowedKinds.includes("video") &&
    !(mutuallyExclusive && imageCount > 0)

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const ids = sorted.map((a) => a.id)
    const oldIndex = ids.indexOf(Number(active.id))
    const newIndex = ids.indexOf(Number(over.id))
    if (oldIndex < 0 || newIndex < 0) return

    const reordered = arrayMove(ids, oldIndex, newIndex)
    router.patch(
      `/pieces/${pieceId}/attachments/reorder`,
      { ids: reordered },
      { preserveScroll: true, preserveState: false }
    )
  }

  return (
    <section>
      <div className="flex items-center justify-between">
        <h3>Attachments</h3>
        <div className="flex gap-2">
          {showImageButton && (
            <FileUploadButton pieceId={pieceId} kind="image" />
          )}
          {showVideoButton && (
            <FileUploadButton
              pieceId={pieceId}
              kind="video"
              label={hasVideo ? "Replace video" : undefined}
            />
          )}
        </div>
      </div>

      {sorted.length === 0 ? (
        <p className="mt-2 text-sm text-ink-muted">
          {editable
            ? "No attachments yet. Use the buttons above to upload files."
            : "No attachments."}
        </p>
      ) : editable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={sorted.map((a) => a.id)} strategy={verticalListSortingStrategy}>
            <ul className="mt-3 flex flex-col gap-2">
              {sorted.map((a) => (
                <Row key={a.id} pieceId={pieceId} attachment={a} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      ) : (
        // Read-only: plain list, no drag handle, no remove.
        <ul className="mt-3 flex flex-col gap-2">
          {sorted.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 rounded-md border border-hairline bg-page p-2"
            >
              <AttachmentThumb attachment={a} />
              <span className="flex-1 truncate text-sm" title={a.filename ?? "Attachment"}>
                {a.filename ?? "Attachment"}
              </span>
              <AttachmentViewer
                url={a.url}
                filename={a.filename}
                kind={a.kind}
                contentType={a.content_type}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function Row({ pieceId, attachment }: { pieceId: number; attachment: AttachmentItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: attachment.id
  })

  const remove = () => {
    if (!window.confirm("Remove this attachment?")) return
    router.delete(`/pieces/${pieceId}/attachments/${attachment.id}`, { preserveScroll: true })
  }

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex items-center gap-2 rounded-md border border-hairline bg-page p-2",
        isDragging && "opacity-60"
      )}
    >
      <button type="button" className="cursor-grab text-ink-muted" {...attributes} {...listeners}>
        <GripVertical className="h-4 w-4" />
      </button>
      <AttachmentThumb attachment={attachment} />
      <span className="flex-1 truncate text-sm" title={attachment.filename ?? "Attachment"}>
        {attachment.filename ?? "Attachment"}
      </span>
      <AttachmentViewer
        url={attachment.url}
        filename={attachment.filename}
        kind={attachment.kind}
        contentType={attachment.content_type}
      />
      <Button variant="ghost" size="sm" onClick={remove} aria-label="Remove">
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  )
}
